import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Toast, type ValidationError } from 'sonata-react-components';

import { FormExpandableSection } from '@/components/common/FormExpandableSection';
import { useTargetingContext } from '@/contexts/Targeting/TargetingContext';
import { AdExchangeDistribuiton } from './AdExchangeDistribuiton/AdExchangeDistribuiton';
import { AdExchangeSelector } from './AdExchangeSelector/AdExchangeSelector';
import { getAllowedAdExchangeCodes } from './helpers/getAllowedAdExchangeCodes.helper';
import { getSupplySourceTags } from './helpers/getSectionHeaderTags.helper';

import type { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';
import type { GoalInterface } from 'sonata-static-storage/lib/schemas/campaign/goal.schema';

import './supplySource.styles.scss';

type State = {
  showErrorToast: boolean;
};

type Props = {
  baseCurrency: BaseCurrencyInterface;
  error?: ValidationError;
  flightsGoals: GoalInterface[];
};

function SupplySource({ baseCurrency, error, flightsGoals }: Props) {
  const { formatMessage } = useIntl();
  const {
    familyType,
    targeting: { adExchanges },
    resetSection,
    user
  } = useTargetingContext();

  const [{ showErrorToast }, setState] = useState<State>({
    showErrorToast: true
  });

  const handleResetSupplySource = () => {
    resetSection('supplySource');
  };

  const allowedAdExchangeCodes = getAllowedAdExchangeCodes(user, familyType);
  const allowedAdExchanges = adExchanges?.filter(ad => allowedAdExchangeCodes.includes(ad.name));
  const isSectionOpened = allowedAdExchanges?.some(ad => ad.blacklisted || ad.distribution !== 0);
  const supplySourceTags = getSupplySourceTags(allowedAdExchanges);

  return (
    <div className="supplySource" id="supplySourceBox">
      <FormExpandableSection
        defaultOpen={isSectionOpened}
        hasErrors={Boolean(error?.message)}
        onReset={handleResetSupplySource}
        preffixId="supplySource"
        tags={supplySourceTags}
        title={formatMessage({
          defaultMessage: 'Supply Source',
          id: 'targeting.supplySource.title'
        })}
        tooltipText={formatMessage({
          defaultMessage:
            'Select the specific ad exchanges or Supply-Side Platforms (SSPs) you want to buy inventory from.',
          id: 'targeting.supplySource.tooltip'
        })}
      >
        <div className="supplySource__boxBody">
          {error?.message && showErrorToast && (
            <Toast
              autoClose={false}
              className="settings__errorToast"
              closeToast={() => setState(prevState => ({ ...prevState, showErrorToast: false }))}
              icon="fal fa-exclamation-triangle"
              text={error?.message}
              type="danger"
            />
          )}
          <div className="supplySource__pageContainer">
            <div className="supplySource__pageContainerSection">
              <AdExchangeSelector />
            </div>
            <div className="supplySource__pageContainerSection">
              <AdExchangeDistribuiton baseCurrency={baseCurrency} flightsGoals={flightsGoals} />
            </div>
          </div>
        </div>
      </FormExpandableSection>
    </div>
  );
}

export default SupplySource;
